import React from 'react';
import styled from 'styled-components';

import {
  AddRestoreJobParameters,
  DeleteArchiveJobParameters,
  ProjectArchiveJob,
} from '../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { RequestParamsToApiPaths } from '../../../../utils/typescript-utils';
import { getHttpStatusCode } from '../../../api/error-handling';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { useFocusElement } from '../../../hooks/use-focus-element';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';

enum Status {
  Idle,
  Pending,
  Error,
}

interface Props {
  latestArchiveJob: ProjectArchiveJob | undefined;
  onClose: (activated: boolean) => void;
}

const RestoreProjectDialog = (props: Props) => {
  const autoFocusRef = useFocusElement<HTMLInputElement>();
  const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
  const { project } = React.useContext(ProjectContext);
  const [status, setStatus] = React.useState(Status.Idle);
  const { handleError, buildErrorList, includeErrorPaths } =
    useErrorHandling<
      RequestParamsToApiPaths<AddRestoreJobParameters | DeleteArchiveJobParameters>
    >();
  const [confirmText, setConfirmText] = React.useState('');

  const restore = async () => {
    try {
      setStatus(Status.Pending);

      // If latest archive job is done, a restore job needs to be created. If it has status idle,
      // we need to remove the archive job and then update the project status. When status is
      // in progress, this dialog isn't displayed.
      if (props.latestArchiveJob) {
        switch (props.latestArchiveJob.status) {
          case 'DONE':
            await projectService.addRestoreJob({ path: { projectId: project.id } });
            publishNotification('succ0320');
            break;

          case 'IDLE':
            try {
              await projectService.deleteArchiveJob({
                path: { projectId: project.id, archiveJobId: props.latestArchiveJob.id },
              });
            } catch (err: unknown) {
              // If deletion fails because job is not found, ignore error. For instance if
              // someone else removed it.
              if (getHttpStatusCode(err) !== 404) {
                throw err;
              }
            }
            await setProjectStatus();
            break;
        }
      } else {
        await setProjectStatus();
      }

      props.onClose(true);
    } catch (err) {
      handleError(err);
      setStatus(Status.Error);
    }
  };

  const setProjectStatus = async () => {
    await projectService.patchProject({
      body: {
        status: 'ACTIVE',
      },
      path: {
        projectId: project.id,
      },
    });
    publishNotification('succ0319');
  };

  const cancel = () => {
    props.onClose(false);
  };

  const isConfirmed = confirmText === project.name;
  const isPartiallyMatch = project.name.startsWith(confirmText);

  return (
    <Dialog
      closeIcon={status === Status.Idle}
      closeOnDimmerClick={status === Status.Idle}
      useGoogleTranslate={true}
      width={500}
      onClose={cancel}
    >
      {{
        header: 'Återställ projekt',
        content: (
          <Content>
            <Stack spacing={1}>
              <span>Vill du påbörja återställning av projektet?</span>

              <InfoBox
                color={isConfirmed ? 'green' : isPartiallyMatch ? 'yellow' : 'red'}
                leftIcon={{ icon: ['fad', isConfirmed ? 'check' : 'pen'] }}
              >
                Skriv in namnet på projektet för att bekräfta.
                <TextBox
                  placeholder={project.name}
                  placeholderVisibleOnMatch={true}
                  ref={autoFocusRef}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                />
              </InfoBox>

              <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'info'] }}>
                {!props.latestArchiveJob || props.latestArchiveJob.status === 'IDLE' ? (
                  <>Återställning av projektet sker omedelbart då projektet inte är arkiverat.</>
                ) : (
                  <>
                    <p>
                      Det kan ta några dagar att återställa projektet.
                      <br />
                      <br />
                      Ett e-postmeddelande skickas till alla administratörer när återställningen är
                      klar.
                    </p>
                  </>
                )}
              </InfoBox>

              {buildErrorList({
                filter: includeErrorPaths(['path.projectId', 'path.archiveJobId']),
                infoBoxProps: {
                  topMargin: false,
                },
              })}
            </Stack>
          </Content>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={status === Status.Pending || !isConfirmed}
                loading={status === Status.Pending}
                variant="contained"
                onClick={restore}
              >
                Återställ
              </Button>
              <Button disabled={status === Status.Pending} variant="text" onClick={cancel}>
                Stäng
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  strong {
    font-weight: 500;
  }
`;

export { RestoreProjectDialog, Props as RestoreProjectDialogProps };
